<script>
    import { onMount } from 'svelte';
    import { contacts } from './store.js';
    import { contactsAPI, dataAPI, labelsAPI } from './api.js';
    import { toast } from './lib/toast.js';

    let showModal = false;
    let editingContact = null;
    // Labels for the contact being edited
    let allLabels = [];
    let contactLabels = [];
    async function loadAllLabels() {
        try { allLabels = await labelsAPI.getAll(); } catch (e) { allLabels = []; }
    }
    async function addContactLabel(labelId) {
        if (!editingContact?.id) return;
        try {
            const updated = await labelsAPI.addToContact(editingContact.id, labelId);
            contactLabels = updated.labels || [];
        } catch (e) { toast.error(e.message || 'No se pudo añadir'); }
    }
    async function removeContactLabel(labelId) {
        if (!editingContact?.id) return;
        try {
            const updated = await labelsAPI.removeFromContact(editingContact.id, labelId);
            contactLabels = updated.labels || [];
        } catch (e) { toast.error(e.message || 'No se pudo quitar'); }
    }
    let formData = {
        name: '',
        email: '',
        phone: '',
        company: '',
        location: '',
        notes: ''
    };

    // CSV import
    let showImport = false;
    let importing = false;
    let importResult = null;
    let importFile = null;

    async function runImport() {
        if (!importFile) return;
        importing = true;
        importResult = null;
        try {
            const res = await contactsAPI.importCsv(importFile);
            importResult = res;
            toast.success(`Importados ${res.created} contactos`, {
                title: res.duplicates ? `${res.duplicates} duplicados omitidos` : undefined
            });
            await loadContacts();
        } catch (err) {
            toast.error(err.message || 'No se pudo importar');
        } finally {
            importing = false;
        }
    }

    async function exportContacts() {
        try {
            await dataAPI.exportContacts('csv');
        } catch (err) {
            toast.error(err.message || 'No se pudo exportar');
        }
    }

    function downloadTemplate() {
        const csv = 'name,email,phone,company,location,notes\nJuan Pérez,juan@example.com,+50688881111,Acme,San José,Cliente VIP\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'plantilla-contactos.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    // Tags (stored at custom_attributes.tags — same key campaigns filter by)
    let tagInput = '';
    let formTags = [];
    let activeTagFilter = '';

    $: allTags = [...new Set(
        $contacts.flatMap(c => (c.custom_attributes || {}).tags || [])
    )].sort();
    $: visibleContacts = activeTagFilter
        ? $contacts.filter(c => ((c.custom_attributes || {}).tags || []).includes(activeTagFilter))
        : $contacts;

    function addTag() {
        const tag = tagInput.trim().toLowerCase();
        if (tag && !formTags.includes(tag)) formTags = [...formTags, tag];
        tagInput = '';
    }
    function removeTag(tag) {
        formTags = formTags.filter(t => t !== tag);
    }
    function onTagKeydown(e) {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
        }
    }

    onMount(async () => {
        await loadContacts();
        await loadAllLabels();
    });

    async function loadContacts() {
        try {
            const data = await contactsAPI.getAll(1, '', 100);
            contacts.set(data.contacts || []);
        } catch (error) {
            console.error('Error loading contacts:', error);
        }
    }

    function openModal(contact = null) {
        editingContact = contact;
        contactLabels = contact ? [...(contact.labels || [])] : [];
        if (contact) {
            formData = { ...contact };
            formTags = [...((contact.custom_attributes || {}).tags || [])];
        } else {
            formData = {
                name: '',
                email: '',
                phone: '',
                company: '',
                location: '',
                notes: ''
            };
            formTags = [];
        }
        tagInput = '';
        showModal = true;
    }

    function closeModal() {
        showModal = false;
        editingContact = null;
    }

    async function handleSubmit() {
        try {
            if (tagInput.trim()) addTag();
            const payload = {
                ...formData,
                custom_attributes: {
                    ...(formData.custom_attributes || {}),
                    tags: formTags
                }
            };
            if (editingContact) {
                await contactsAPI.update(editingContact.id, payload);
            } else {
                await contactsAPI.create(payload);
            }
            closeModal();
            await loadContacts();
        } catch (error) {
            console.error('Error saving contact:', error);
            toast.error('No se pudo guardar el contacto');
        }
    }

    async function handleDelete(id) {
        if (!confirm('Are you sure you want to delete this contact?')) return;

        try {
            await contactsAPI.delete(id);
            await loadContacts();
        } catch (error) {
            console.error('Error deleting contact:', error);
            toast.error('No se pudo eliminar');
        }
    }
</script>

<div class="contacts-container">
    <div class="contacts-header">
        <h2>Contactos</h2>
        <div class="header-actions">
            <button class="btn btn-secondary btn-sm" on:click={exportContacts}>
                Exportar CSV
            </button>
            <button class="btn btn-secondary btn-sm" on:click={() => { showImport = true; importResult = null; importFile = null; }}>
                Importar CSV
            </button>
            <button class="btn btn-primary btn-sm" on:click={() => openModal()}>
                + Nuevo contacto
            </button>
        </div>
    </div>

    {#if allTags.length}
        <div class="tag-filter-row">
            <button class="tag-pill" class:on={activeTagFilter === ''}
                on:click={() => (activeTagFilter = '')}>Todos</button>
            {#each allTags as tag}
                <button class="tag-pill" class:on={activeTagFilter === tag}
                    on:click={() => (activeTagFilter = activeTagFilter === tag ? '' : tag)}>{tag}</button>
            {/each}
        </div>
    {/if}

    <div class="contacts-grid">
        {#if visibleContacts.length === 0}
            <div class="empty-state">
                <p>{activeTagFilter ? 'Sin contactos con esa etiqueta.' : 'No contacts yet. Add your first contact!'}</p>
            </div>
        {:else}
            {#each visibleContacts as contact (contact.id)}
                <div class="contact-card">
                    <div class="contact-avatar">
                        {contact.name?.charAt(0) || '?'}
                    </div>
                    <div class="contact-details">
                        <h3>{contact.name}</h3>
                        {#if contact.email}
                            <p class="contact-email">{contact.email}</p>
                        {/if}
                        {#if contact.phone}
                            <p class="contact-phone">{contact.phone}</p>
                        {/if}
                        {#if contact.company}
                            <p class="contact-company">{contact.company}</p>
                        {/if}
                        {#if ((contact.custom_attributes || {}).tags || []).length}
                            <div class="card-tags">
                                {#each (contact.custom_attributes || {}).tags as tag}
                                    <span class="mini-tag">{tag}</span>
                                {/each}
                            </div>
                        {/if}
                    </div>
                    <div class="contact-actions">
                        <button class="btn-edit" on:click={() => openModal(contact)}>
                            Edit
                        </button>
                        <button class="btn-delete" on:click={() => handleDelete(contact.id)}>
                            Delete
                        </button>
                    </div>
                </div>
            {/each}
        {/if}
    </div>
</div>

{#if showModal}
    <div class="modal-overlay" on:click={closeModal}>
        <div class="modal" on:click|stopPropagation>
            <div class="modal-header">
                <h3>{editingContact ? 'Edit Contact' : 'Add Contact'}</h3>
                <button class="close-button" on:click={closeModal}>×</button>
            </div>

            <form on:submit|preventDefault={handleSubmit} class="modal-form">
                <div class="form-group">
                    <label for="name">Name *</label>
                    <input
                        type="text"
                        id="name"
                        bind:value={formData.name}
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        bind:value={formData.email}
                    />
                </div>

                <div class="form-group">
                    <label for="phone">Phone</label>
                    <input
                        type="tel"
                        id="phone"
                        bind:value={formData.phone}
                    />
                </div>

                <div class="form-group">
                    <label for="company">Company</label>
                    <input
                        type="text"
                        id="company"
                        bind:value={formData.company}
                    />
                </div>

                <div class="form-group">
                    <label for="location">Location</label>
                    <input
                        type="text"
                        id="location"
                        bind:value={formData.location}
                    />
                </div>

                <div class="form-group">
                    <label for="notes">Notes</label>
                    <textarea
                        id="notes"
                        bind:value={formData.notes}
                        rows="3"
                    ></textarea>
                </div>

                <div class="form-group">
                    <label for="tags">Etiquetas <span class="label-hint">(para segmentar campañas)</span></label>
                    <div class="tags-editor">
                        {#each formTags as tag}
                            <span class="chip">
                                {tag}
                                <button type="button" class="chip-x" on:click={() => removeTag(tag)}>×</button>
                            </span>
                        {/each}
                        <input
                            type="text"
                            id="tags"
                            bind:value={tagInput}
                            on:keydown={onTagKeydown}
                            on:blur={addTag}
                            placeholder={formTags.length ? '' : 'vip, cliente, interesado...'}
                            class="tag-input"
                        />
                    </div>
                </div>

                {#if editingContact?.id}
                    <div class="form-group">
                        <label>Etiquetas de la plataforma</label>
                        <div class="tags-editor">
                            {#each contactLabels as lb (lb.id)}
                                <span class="chip" style="background:{lb.color}; color:#fff; border:none">
                                    {lb.name}
                                    <button type="button" class="chip-x" on:click={() => removeContactLabel(lb.id)}>×</button>
                                </span>
                            {/each}
                        </div>
                        <select class="label-select" on:change={(e) => { if (e.target.value) { addContactLabel(parseInt(e.target.value)); e.target.value=''; } }}>
                            <option value="">+ Añadir etiqueta…</option>
                            {#each allLabels.filter(l => !contactLabels.some(x => x.id === l.id)) as l (l.id)}
                                <option value={l.id}>{l.name}</option>
                            {/each}
                        </select>
                    </div>
                {/if}

                <div class="modal-actions">
                    <button type="button" class="btn-cancel" on:click={closeModal}>
                        Cancel
                    </button>
                    <button type="submit" class="btn-submit">
                        {editingContact ? 'Update' : 'Create'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}

{#if showImport}
    <div class="modal-overlay" on:click={() => (showImport = false)}>
        <div class="modal-content import-modal" on:click|stopPropagation>
            <div class="modal-header">
                <h3>Importar contactos desde CSV</h3>
                <button class="close-button" on:click={() => (showImport = false)}>×</button>
            </div>
            <div class="import-body">
                <p class="import-help">
                    Archivo CSV con columnas: <code>name, email, phone, company, location, notes</code>.
                    Máximo 5000 filas. Se omiten duplicados (por email o teléfono).
                </p>
                <button class="link-btn" on:click={downloadTemplate}>↓ Descargar plantilla</button>

                <label class="file-drop">
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        on:change={(e) => importFile = e.target.files[0]}
                    />
                    {#if importFile}
                        <span class="file-name">📄 {importFile.name}</span>
                    {:else}
                        <span class="file-hint">Haz clic para seleccionar un .csv</span>
                    {/if}
                </label>

                {#if importResult}
                    <div class="import-result">
                        <div class="result-row"><b>{importResult.created}</b> creados</div>
                        <div class="result-row muted">{importResult.duplicates} duplicados</div>
                        <div class="result-row muted">{importResult.skipped} omitidos</div>
                        {#if importResult.errors?.length}
                            <details>
                                <summary>{importResult.errors.length} errores</summary>
                                <ul>
                                    {#each importResult.errors as e}
                                        <li>Fila {e.row}: {e.error}</li>
                                    {/each}
                                </ul>
                            </details>
                        {/if}
                    </div>
                {/if}

                <div class="form-actions">
                    <button type="button" class="btn-cancel" on:click={() => (showImport = false)}>
                        {importResult ? 'Cerrar' : 'Cancelar'}
                    </button>
                    <button
                        type="button"
                        class="btn-submit"
                        disabled={!importFile || importing}
                        on:click={runImport}
                    >
                        {importing ? 'Importando...' : 'Importar'}
                    </button>
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    /* Tags */
    .tag-filter-row {
        display: flex; flex-wrap: wrap; gap: 6px;
        margin: 0 0 14px;
    }
    .tag-pill {
        padding: 4px 12px;
        border: 1px solid var(--border-2);
        border-radius: 9999px;
        font-size: 12.5px;
        color: var(--text-2);
        background: var(--surface-0);
        cursor: pointer;
        transition: all var(--transition-fast);
    }
    .tag-pill:hover { border-color: var(--accent); }
    .tag-pill.on { background: var(--accent); border-color: var(--accent); color: #fff; }

    .card-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
    .mini-tag {
        font-size: 10.5px; font-weight: 600;
        padding: 1px 8px;
        border-radius: 9999px;
        background: var(--accent-soft);
        color: var(--accent);
    }

    .label-hint { font-weight: 400; color: var(--text-3); font-size: 11.5px; }
    .tags-editor {
        display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
        border: 1px solid var(--border-2);
        border-radius: var(--radius);
        padding: 6px 8px;
        background: var(--surface-0);
    }
    .tags-editor:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
    .chip {
        display: inline-flex; align-items: center; gap: 4px;
        background: var(--accent-soft);
        color: var(--accent);
        font-size: 12px; font-weight: 600;
        padding: 2px 8px;
        border-radius: 9999px;
    }
    .chip-x { color: var(--accent); font-size: 14px; line-height: 1; cursor: pointer; }
    .tag-input {
        flex: 1; min-width: 120px;
        border: none !important; box-shadow: none !important;
        padding: 4px !important;
    }

    .header-actions { display: flex; gap: 8px; }
    .import-modal { max-width: 480px; }
    .import-body { padding: 16px 20px 20px; display: flex; flex-direction: column; gap: 14px; }
    .import-help { font-size: 13px; color: var(--text-2); line-height: 1.5; margin: 0; }
    .import-help code {
        font-family: var(--font-mono);
        font-size: 11.5px;
        background: var(--surface-2);
        padding: 1px 5px;
        border-radius: 4px;
    }
    .link-btn {
        background: transparent;
        color: var(--accent);
        font-size: 12.5px;
        text-align: left;
        padding: 0;
        cursor: pointer;
        text-decoration: underline;
    }
    .file-drop {
        position: relative;
        display: flex; align-items: center; justify-content: center;
        padding: 22px;
        border: 2px dashed var(--border-2);
        border-radius: 8px;
        cursor: pointer;
        transition: border-color var(--transition-fast), background var(--transition-fast);
        font-size: 13px;
        color: var(--text-2);
    }
    .file-drop:hover { border-color: var(--accent); background: var(--accent-soft); }
    .file-drop input[type="file"] {
        position: absolute; inset: 0;
        opacity: 0; cursor: pointer;
    }
    .file-name { font-weight: 600; color: var(--text-1); }
    .file-hint { color: var(--text-3); }
    .import-result {
        padding: 12px;
        background: var(--surface-1);
        border-radius: 8px;
        font-size: 13px;
    }
    .result-row { padding: 2px 0; }
    .result-row.muted { color: var(--text-3); }
    .import-result details { margin-top: 8px; font-size: 12px; }
    .import-result ul { padding-left: 18px; margin: 6px 0 0; }

    .contacts-container {
        padding: 2rem;
        height: 100%;
        overflow-y: auto;
        background: #f5f7fb;
    }

    .contacts-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 2rem;
    }

    .contacts-header h2 {
        margin: 0;
        font-size: 1.75rem;
        color: #333;
    }

    .btn-primary {
        padding: 0.75rem 1.5rem;
        background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 0.9375rem;
        font-weight: 600;
        transition: transform 0.2s;
    }

    .btn-primary:hover {
        transform: translateY(-2px);
    }

    .contacts-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
        gap: 1.5rem;
    }

    .empty-state {
        grid-column: 1 / -1;
        text-align: center;
        padding: 3rem;
        color: #999;
    }

    .contact-card {
        background: white;
        padding: 1.5rem;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .contact-avatar {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 1.5rem;
        margin: 0 auto;
    }

    .contact-details {
        text-align: center;
    }

    .contact-details h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1.125rem;
        color: #333;
    }

    .contact-email,
    .contact-phone,
    .contact-company {
        margin: 0.25rem 0;
        font-size: 0.875rem;
        color: #666;
    }

    .contact-actions {
        display: flex;
        gap: 0.5rem;
        margin-top: auto;
    }

    .btn-edit,
    .btn-delete {
        flex: 1;
        padding: 0.625rem;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 500;
        transition: opacity 0.2s;
    }

    .btn-edit {
        background: #e3f2fd;
        color: #1976d2;
    }

    .btn-delete {
        background: #ffebee;
        color: #d32f2f;
    }

    .btn-edit:hover,
    .btn-delete:hover {
        opacity: 0.8;
    }

    /* Modal */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        border-radius: 12px;
        width: 90%;
        max-width: 500px;
        max-height: 90vh;
        overflow-y: auto;
    }

    .modal-header {
        padding: 1.5rem;
        border-bottom: 1px solid #e0e0e0;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .modal-header h3 {
        margin: 0;
        font-size: 1.25rem;
        color: #333;
    }

    .close-button {
        background: none;
        border: none;
        font-size: 2rem;
        color: #999;
        cursor: pointer;
        line-height: 1;
        padding: 0;
        width: 32px;
        height: 32px;
    }

    .modal-form {
        padding: 1.5rem;
    }

    .form-group {
        margin-bottom: 1.25rem;
    }

    .form-group label {
        display: block;
        margin-bottom: 0.5rem;
        color: #333;
        font-weight: 500;
        font-size: 0.9rem;
    }

    .form-group input,
    .form-group textarea {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        font-size: 0.9375rem;
        font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus {
        outline: none;
        border-color: #128C7E;
    }

    .modal-actions {
        display: flex;
        gap: 1rem;
        margin-top: 1.5rem;
    }

    .btn-cancel,
    .btn-submit {
        flex: 1;
        padding: 0.75rem;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.9375rem;
        font-weight: 600;
    }

    .btn-cancel {
        background: #f5f5f5;
        color: #666;
    }

    .btn-submit {
        background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
        color: white;
    }

    .btn-cancel:hover,
    .btn-submit:hover {
        opacity: 0.9;
    }
</style>
